import React, { useState, useCallback } from 'react';
import { DEMO_SAMPLE_LOG, SampleRecord } from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';
import ViewModeToggle from '../components/ViewModeToggle';

const SAMPLE_GLOSSARY: GlossaryEntry[] = [
  { term: 'PK', definition: 'Pharmacokinetic \u2014 measures drug concentration in blood over time' },
  { term: 'PD', definition: 'Pharmacodynamic \u2014 measures the drug\u2019s biological effect on target' },
  { term: 'Germline DNA', definition: 'Inherited genetic material \u2014 used for pharmacogenomic profiling' },
  { term: 'ctDNA', definition: 'Circulating tumor DNA \u2014 liquid biopsy biomarker' },
  { term: 'ECG', definition: 'Electrocardiogram \u2014 12-lead heart electrical activity recording' },
  { term: 'CSF', definition: 'Cerebrospinal fluid \u2014 collected via lumbar puncture' },
  { term: 'EDTA', definition: 'Anticoagulant tube type for DNA/blood samples' },
  { term: 'Chain of custody', definition: 'Documented trail from collection to lab \u2014 must be unbroken for regulatory acceptance' },
  { term: 'Temperature excursion', definition: 'Sample stored outside protocol range \u2014 may compromise results' },
  { term: 'Hemolyzed', definition: 'Red blood cell breakdown \u2014 sample may give inaccurate results' },
  { term: 'EDC delay', definition: 'Time between result availability and entry into Electronic Data Capture system' },
  { term: 'Turnaround time', definition: 'Hours from sample collection to result availability' },
  { term: 'SHA-256', definition: 'Cryptographic hash \u2014 unique fingerprint ensuring tamper-evidence per 21 CFR Part 11' },
  { term: '21 CFR Part 11', definition: 'FDA regulation for electronic records and signatures in clinical trials' },
];

const SITE_COLORS: Record<string, string> = {
  'UCLA': '#38b2ac',
  'UCSF': '#4299e1',
  'Johns Hopkins': '#ed8936',
};

// Deterministic hash generator from input string
function generateHash(input: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  const hex = (v: number) => (v >>> 0).toString(16).padStart(8, '0');
  // Generate 64 hex chars (256 bits) by running multiple rounds
  let result = '';
  for (let round = 0; round < 8; round++) {
    h ^= round * 0x9e3779b9;
    h = Math.imul(h, 0x01000193);
    result += hex(h);
  }
  return result;
}

function sampleHash(s: SampleRecord): string {
  return generateHash(`${s.id}|${s.patientId}|${s.actualTime}|${s.collectedBy}|${s.volume}|${s.storageTempC}|${s.chainOfCustody}|${s.status}`);
}

interface AuditEvent {
  step: string;
  timestamp: string;
  person: string;
  action: string;
  hash: string;
}

function buildAuditTrail(s: SampleRecord): AuditEvent[] {
  const events: AuditEvent[] = [
    { step: 'Collection', timestamp: s.actualTime, person: s.collectedBy, action: `Collected ${s.volume} ${s.type} at ${s.storageTempC}\u00B0C`, hash: generateHash(`collect|${s.id}|${s.actualTime}|${s.collectedBy}`) },
    { step: 'Storage', timestamp: s.actualTime, person: s.collectedBy, action: `Stored at ${s.storageTempC}\u00B0C \u2014 chain ${s.chainOfCustody.toLowerCase()}`, hash: generateHash(`store|${s.id}|${s.storageTempC}|${s.chainOfCustody}`) },
    { step: 'Transport', timestamp: s.transportTime, person: 'Courier', action: `Transported to central lab`, hash: generateHash(`transport|${s.id}|${s.transportTime}`) },
    { step: 'Lab Receipt', timestamp: s.labReceived, person: 'Lab Tech', action: `Received after ${s.transitHours.toFixed(1)}h transit`, hash: generateHash(`receipt|${s.id}|${s.labReceived}|${s.transitHours}`) },
    { step: 'Processing', timestamp: s.processingStarted, person: 'Lab Analyst', action: `Processing initiated for ${s.type}`, hash: generateHash(`process|${s.id}|${s.processingStarted}|${s.type}`) },
    { step: 'Result', timestamp: s.resultAvailable, person: 'Lab Director', action: `Result available \u2014 turnaround ${s.turnaroundHours.toFixed(1)}h`, hash: generateHash(`result|${s.id}|${s.resultAvailable}|${s.turnaroundHours}`) },
    { step: 'EDC Entry', timestamp: s.edcEntry, person: 'Data Manager', action: `Entered in EDC \u2014 ${s.edcDelayHours.toFixed(1)}h after result`, hash: generateHash(`edc|${s.id}|${s.edcEntry}|${s.edcDelayHours}`) },
  ];
  return events;
}

function hashVerified(s: SampleRecord): boolean {
  return s.chainOfCustody === 'Intact' && s.status === 'Complete';
}

function delayColor(min: number): string {
  if (min <= 15) return '#48bb78';
  if (min <= 60) return '#ecc94b';
  return '#f56565';
}

function statusStyle(status: string): { bg: string; text: string } {
  switch (status) {
    case 'Complete': return { bg: '#22543d', text: '#48bb78' };
    case 'Pending': return { bg: '#744210', text: '#ecc94b' };
    case 'Rejected': return { bg: '#742a2a', text: '#fc8181' };
    case 'Hemolyzed': return { bg: '#742a2a', text: '#fc8181' };
    case 'Insufficient volume': return { bg: '#742a2a', text: '#fc8181' };
    default: return { bg: '#2d3748', text: '#a0aec0' };
  }
}

function chainStyleFn(chain: string): { bg: string; text: string } {
  switch (chain) {
    case 'Intact': return { bg: '#22543d', text: '#48bb78' };
    case 'Temperature excursion': return { bg: '#742a2a', text: '#fc8181' };
    case 'Broken': return { bg: '#742a2a', text: '#fc8181' };
    default: return { bg: '#2d3748', text: '#a0aec0' };
  }
}

type ViewTab = 'collection' | 'lab' | 'edc';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [text]);
  return (
    <button onClick={handleCopy} style={styles.copyBtn}>
      {copied ? '\u2713' : '\u2398'}
    </button>
  );
}

export default function SampleLog() {
  const [tab, setTab] = useState<ViewTab>('collection');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const total = DEMO_SAMPLE_LOG.length;
  const complete = DEMO_SAMPLE_LOG.filter((s) => s.status === 'Complete').length;
  const pending = DEMO_SAMPLE_LOG.filter((s) => s.status === 'Pending').length;
  const rejected = DEMO_SAMPLE_LOG.filter((s) => s.status === 'Rejected' || s.status === 'Hemolyzed' || s.status === 'Insufficient volume').length;
  const chainBreaks = DEMO_SAMPLE_LOG.filter((s) => s.chainOfCustody !== 'Intact').length;
  const avgTurnaround = (DEMO_SAMPLE_LOG.reduce((sum, s) => sum + s.turnaroundHours, 0) / total).toFixed(1);

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <h2 style={styles.title}>Sample Accountability Log</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ViewModeToggle />
          <Glossary entries={SAMPLE_GLOSSARY} />
        </div>
      </div>
      <p style={styles.desc}>
        Complete chain-of-custody tracking for all biological samples from collection through
        lab processing to EDC entry. Each event is SHA-256 hashed per 21 CFR Part 11.
      </p>
      <ComplianceBadges badges={["21 CFR Part 11", "ICH E6(R3)", "ALCOA", "MHRA Apr 2026"]} />

      {/* Summary cards */}
      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Total Samples</div>
          <div style={styles.summaryValue}>{total}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Complete</div>
          <div style={{ ...styles.summaryValue, color: '#48bb78' }}>{complete}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Pending</div>
          <div style={{ ...styles.summaryValue, color: '#ecc94b' }}>{pending}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Rejected</div>
          <div style={{ ...styles.summaryValue, color: rejected > 0 ? '#f56565' : '#48bb78' }}>{rejected}</div>
        </div>
        <div style={{ ...styles.summaryCard, border: chainBreaks > 0 ? '1px solid #f56565' : '1px solid #2d3748' }}>
          <div style={styles.summaryLabel}>Chain Breaks</div>
          <div style={{ ...styles.summaryValue, color: chainBreaks > 0 ? '#f56565' : '#48bb78' }}>{chainBreaks}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Avg Turnaround</div>
          <div style={styles.summaryValue}>{avgTurnaround}h</div>
        </div>
      </div>

      {/* Tab switcher */}
      <div style={styles.tabRow}>
        {([
          { key: 'collection' as ViewTab, label: 'Collection & Status' },
          { key: 'lab' as ViewTab, label: 'Lab Processing' },
          { key: 'edc' as ViewTab, label: 'EDC & Turnaround' },
        ]).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              ...styles.tabBtn,
              backgroundColor: tab === t.key ? '#2b6cb0' : '#2d3748',
              color: tab === t.key ? '#fff' : '#a0aec0',
            }}
          >{t.label}</button>
        ))}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}></th>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Patient</th>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Visit</th>
              {tab === 'collection' && (
                <>
                  <th style={styles.th}>Scheduled</th>
                  <th style={styles.th}>Actual</th>
                  <th style={styles.th}>Delay</th>
                  <th style={styles.th}>Collected By</th>
                  <th style={styles.th}>Volume</th>
                  <th style={styles.th}>Temp</th>
                  <th style={styles.th}>Chain</th>
                  <th style={styles.th}>Status</th>
                </>
              )}
              {tab === 'lab' && (
                <>
                  <th style={styles.th}>Transport</th>
                  <th style={styles.th}>Lab Received</th>
                  <th style={styles.th}>Transit</th>
                  <th style={styles.th}>Processing</th>
                  <th style={styles.th}>Result</th>
                  <th style={styles.th}>Turnaround</th>
                  <th style={styles.th}>Status</th>
                </>
              )}
              {tab === 'edc' && (
                <>
                  <th style={styles.th}>Result Available</th>
                  <th style={styles.th}>EDC Entry</th>
                  <th style={styles.th}>EDC Delay</th>
                  <th style={styles.th}>Turnaround</th>
                  <th style={styles.th}>Hash</th>
                  <th style={styles.th}>Chain</th>
                  <th style={styles.th}>Status</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {DEMO_SAMPLE_LOG.map((s) => {
              const sColor = SITE_COLORS[s.site] || '#a0aec0';
              const st = statusStyle(s.status);
              const ch = chainStyleFn(s.chainOfCustody);
              const isRed = s.status === 'Rejected' || s.status === 'Hemolyzed' || s.chainOfCustody !== 'Intact';
              const isExpanded = expandedId === s.id;
              const hash = sampleHash(s);
              const verified = hashVerified(s);
              const colSpan = tab === 'collection' ? 14 : tab === 'lab' ? 13 : 13;

              return (
                <React.Fragment key={s.id}>
                  <tr style={isRed ? { backgroundColor: 'rgba(245, 101, 101, 0.06)' } : {}} onClick={() => setExpandedId(isExpanded ? null : s.id)}>
                    <td style={{ ...styles.td, cursor: 'pointer', width: 24, color: '#718096', fontSize: 11 }}>
                      {isExpanded ? '\u25B2' : '\u25BC'}
                    </td>
                    <td style={styles.td}><span style={{ color: '#63b3ed', fontWeight: 600 }}>{s.id}</span></td>
                    <td style={styles.td}><span style={{ fontWeight: 600, fontSize: 12 }}>{s.type}</span></td>
                    <td style={styles.td}>{s.patientId}</td>
                    <td style={styles.td}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                        backgroundColor: sColor + '22', color: sColor, border: `1px solid ${sColor}`,
                      }}>{s.site}</span>
                    </td>
                    <td style={styles.td}>V{s.visit}</td>
                    {tab === 'collection' && (
                      <>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.scheduledTime).toLocaleString()}</td>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.actualTime).toLocaleString()}</td>
                        <td style={styles.td}>
                          <span style={{ color: delayColor(s.delayMin), fontWeight: 600 }}>
                            {s.delayMin === 0 ? 'On time' : `+${s.delayMin}m`}
                          </span>
                        </td>
                        <td style={{ ...styles.td, fontSize: 12 }}>{s.collectedBy}</td>
                        <td style={styles.td}>{s.volume}</td>
                        <td style={styles.td}>
                          <span style={{ color: s.storageTempC === -60 ? '#f56565' : '#a0aec0' }}>
                            {s.storageTempC}&deg;C
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: ch.bg, color: ch.text }}>
                            {s.chainOfCustody}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: st.bg, color: st.text }}>
                            {s.status}
                          </span>
                        </td>
                      </>
                    )}
                    {tab === 'lab' && (
                      <>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.transportTime).toLocaleString()}</td>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.labReceived).toLocaleString()}</td>
                        <td style={styles.td}>{s.transitHours.toFixed(1)}h</td>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.processingStarted).toLocaleString()}</td>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.resultAvailable).toLocaleString()}</td>
                        <td style={styles.td}>
                          <span style={{ color: s.turnaroundHours <= 6 ? '#48bb78' : s.turnaroundHours <= 12 ? '#ecc94b' : '#a0aec0', fontWeight: 600 }}>
                            {s.turnaroundHours.toFixed(1)}h
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: st.bg, color: st.text }}>
                            {s.status}
                          </span>
                        </td>
                      </>
                    )}
                    {tab === 'edc' && (
                      <>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.resultAvailable).toLocaleString()}</td>
                        <td style={{ ...styles.td, fontSize: 11 }}>{new Date(s.edcEntry).toLocaleString()}</td>
                        <td style={styles.td}>
                          <span style={{ color: s.edcDelayHours <= 1 ? '#48bb78' : s.edcDelayHours <= 4 ? '#ecc94b' : '#f56565', fontWeight: 600 }}>
                            {s.edcDelayHours.toFixed(1)}h
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ color: s.turnaroundHours <= 6 ? '#48bb78' : s.turnaroundHours <= 12 ? '#ecc94b' : '#a0aec0', fontWeight: 600 }}>
                            {s.turnaroundHours.toFixed(1)}h
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <code style={styles.hashCode}>{hash.slice(0, 16)}...</code>
                            <CopyButton text={hash} />
                          </div>
                          <div style={{ marginTop: 3 }}>
                            {verified ? (
                              <span style={styles.hashVerified}>Hash verified</span>
                            ) : (
                              <span style={styles.hashMismatch}>Hash mismatch</span>
                            )}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: ch.bg, color: ch.text }}>
                            {s.chainOfCustody}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600, backgroundColor: st.bg, color: st.text }}>
                            {s.status}
                          </span>
                        </td>
                      </>
                    )}
                  </tr>
                  {/* Expanded audit trail */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={colSpan} style={{ padding: 0, border: 'none' }}>
                        <div style={styles.auditPanel}>
                          {/* Sample hash */}
                          <div style={styles.auditHashRow}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                              <span style={styles.auditHashLabel}>SHA-256:</span>
                              <code style={styles.hashCodeFull}>{hash}</code>
                              <CopyButton text={hash} />
                            </div>
                            {verified ? (
                              <span style={styles.hashVerified}>Hash verified</span>
                            ) : (
                              <span style={styles.hashMismatch}>Hash mismatch</span>
                            )}
                          </div>

                          {/* Audit trail timeline */}
                          <div style={styles.auditTitle}>Sample Audit Trail</div>
                          <div style={styles.timeline}>
                            {buildAuditTrail(s).map((evt, i) => (
                              <div key={evt.step} style={styles.timelineItem}>
                                <div style={styles.timelineDot}>
                                  <div style={{
                                    ...styles.dot,
                                    backgroundColor: i === buildAuditTrail(s).length - 1 ? '#63b3ed' : '#48bb78',
                                  }} />
                                  {i < buildAuditTrail(s).length - 1 && <div style={styles.timelineLine} />}
                                </div>
                                <div style={styles.timelineContent}>
                                  <div style={styles.timelineStep}>{evt.step}</div>
                                  <div style={styles.timelineTime}>{new Date(evt.timestamp).toLocaleString()}</div>
                                  <div style={styles.timelinePerson}>{evt.person}</div>
                                  <div style={styles.timelineAction}>{evt.action}</div>
                                  <div style={styles.timelineHash}>
                                    <code style={styles.hashCodeSmall}>{evt.hash.slice(0, 20)}...</code>
                                    <CopyButton text={evt.hash} />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 24, maxWidth: 700 },
  summaryRow: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' },
  summaryCard: { backgroundColor: '#1a202c', borderRadius: 8, padding: '10px 16px', minWidth: 110, flex: 1, border: '1px solid #2d3748' },
  summaryLabel: { color: '#718096', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 },
  summaryValue: { color: '#e2e8f0', fontSize: 24, fontWeight: 700, marginTop: 2 },
  tabRow: { display: 'flex', gap: 8, marginBottom: 16 },
  tabBtn: { padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, transition: 'all 0.15s' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #2d3748', color: '#a0aec0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' },
  td: { padding: '8px 10px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 12 },

  // Hash styles
  hashCode: { color: '#48bb78', fontSize: 10, fontFamily: 'monospace', backgroundColor: '#171923', padding: '2px 6px', borderRadius: 4 },
  hashCodeFull: { color: '#48bb78', fontSize: 10, fontFamily: 'monospace', backgroundColor: '#171923', padding: '3px 8px', borderRadius: 4, wordBreak: 'break-all' as const },
  hashCodeSmall: { color: '#718096', fontSize: 9, fontFamily: 'monospace', backgroundColor: '#171923', padding: '1px 4px', borderRadius: 3 },
  copyBtn: { background: 'none', border: '1px solid #2d3748', borderRadius: 4, color: '#a0aec0', fontSize: 11, cursor: 'pointer', padding: '1px 5px', lineHeight: 1 },
  hashVerified: { backgroundColor: '#22543d', color: '#48bb78', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700 },
  hashMismatch: { backgroundColor: '#742a2a', color: '#fc8181', padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 700 },

  // Audit panel
  auditPanel: { backgroundColor: '#171923', padding: '16px 20px', margin: '0 0 1px', borderTop: '1px solid #2d3748' },
  auditHashRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' as const },
  auditHashLabel: { color: '#a0aec0', fontSize: 11, fontWeight: 600 },
  auditTitle: { color: '#cbd5e0', fontSize: 12, fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 12 },

  // Timeline
  timeline: { position: 'relative' as const },
  timelineItem: { display: 'flex', gap: 12, marginBottom: 2 },
  timelineDot: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', width: 16, flexShrink: 0 },
  dot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  timelineLine: { width: 2, flex: 1, backgroundColor: '#2d3748', minHeight: 24 },
  timelineContent: { paddingBottom: 14, flex: 1 },
  timelineStep: { color: '#e2e8f0', fontSize: 12, fontWeight: 700 },
  timelineTime: { color: '#718096', fontSize: 10, marginTop: 1 },
  timelinePerson: { color: '#63b3ed', fontSize: 11, marginTop: 2 },
  timelineAction: { color: '#a0aec0', fontSize: 11, marginTop: 1 },
  timelineHash: { display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 },
};
